<template>
  <MDBContainer class="p-0">
    <Loader :show="loading" v-if="loading" />
    <MDBCard v-else>
      <MDBCardBody>
        <MDBCardTitle>{{ $t("shop.shoppingCart") }}</MDBCardTitle>
        <MDBCardText>
          <div class="mt-5" v-if="cart?.items?.length > 0">
            <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <div class="d-flex flex-wrap gap-3">
                <span
                  class="text-decoration-underline cursor--pointer"
                  :class="{ 'opacity-50': pending }"
                  @click="selectAllItems"
                >
                  {{ $t("global.buttons.selectAll") }}
                </span>
                <span
                  class="text-decoration-underline cursor--pointer"
                  :class="{ 'opacity-50': pending }"
                  @click="deselectAllItems"
                >
                  {{ $t("global.buttons.deselectAll") }}
                </span>
              </div>
              <div class="d-flex flex-wrap gap-2">
                <MDBBtn
                  outline="danger"
                  size="sm"
                  :disabled="pending || selectedItemTokens.length === 0"
                  @click="openRemoveSelectedModal"
                >
                  {{ $t("shop.removeSelected") }}
                </MDBBtn>
                <MDBBtn outline="danger" size="sm" :disabled="pending" @click="openClearCartModal">
                  {{ $t("shop.clearCart") }}
                </MDBBtn>
              </div>
            </div>
            <MDBTable>
              <thead>
                <tr>
                  <th scope="col" class="cart-select-col"></th>
                  <th scope="col">{{ $t("shop.product") }}</th>
                  <th scope="col">{{ $t("shop.quantity") }}</th>
                  <th scope="col">{{ $t("shop.attributes") }}</th>
                  <th scope="col">{{ $t("shop.cart.pointsPerUnit") }}</th>
                  <th scope="col">{{ $t("shop.cart.pointsSum") }}</th>
                  <th scope="col">{{ $t("shop.action") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in cart.items" :key="`product_${i}`">
                  <th scope="row" class="cart-select-col">
                    <input
                      v-model="selectedItemTokens"
                      class="form-check-input"
                      type="checkbox"
                      :value="item.token"
                      :disabled="pending"
                    />
                  </th>
                  <th scope="row">
                    <div class="d-flex flex-column">
                      <router-link :to="{
                        name: 'productDetails',
                        params: {
                          id: item.product.id,
                        },
                      }">
                        {{ getProductName(item.product) }}
                      </router-link>
                      <span v-if="isItemDeleted(item)" class="text-danger small">
                        {{ $t("shop.productUnavailable") }}
                      </span>
                    </div>
                  </th>
                  <th scope="row">
                    <NumberInput
                      label=""
                      :step="1"
                      :min="1"
                      :max="getMaxQuantityForItem(item)"
                      v-model="item.quantity"
                      @maxReached="showNotEnoughPointsToast"
                      @update:modelValue="(quantity) => changeQuantity(quantity, item)"
                    />
                  </th>
                  <th scope="row">
                    {{ showAttributes(item) }}
                  </th>
                  <th scope="row">{{ showPoints(item) }}</th>
                  <th scope="row">{{ item.points }}</th>
                  <th scope="row">
                    <MDBBtn outline="danger" rounded @click.prevent="openRemoveModal(item)" :disabled="pending">{{
                      $t("global.buttons.clear") }}</MDBBtn>
                  </th>
                </tr>
                <tr>
                  <th scope="row" colspan="5"></th>
                  <th scope="row" colspan="3">
                    <p>
                      <strong>{{ cart.total }}</strong>
                      {{ $t("shop.pointsTotal") }}
                    </p>
                    <p>
                      <strong>{{ user.points?.avail_points - cart.total }} </strong>
                      {{ $t("shop.cartRemainPoints") }}
                    </p>
                  </th>
                </tr>
              </tbody>
            </MDBTable>
          </div>
          <div v-else>
            {{ $t("shop.emptyCart") }}
            <router-link :to="{ name: 'userOrders' }">
              {{ $t("global.nav.userOrders") }} </router-link>.
          </div>
        </MDBCardText>
      </MDBCardBody>
      <MDBCardFooter class="text-muted" v-if="cart?.items?.length > 0">
        <MDBBtn color="success" @click="handleContinue" :disabled="!overPoints || pending">{{
          $t("global.buttons.toOrderDelivery")
          }}
        </MDBBtn>
        <MDBBtn color="primary" @click="back()" :disabled="pending">{{ $t("global.buttons.back") }}</MDBBtn>
      </MDBCardFooter>
    </MDBCard>
    <RemoveItemModal
      v-model="showRemoveModal"
      :item-name="removeItemName"
      :message="removeModalMessage"
      :item-names="removeModalItemNames"
      :pending="pending"
      @confirm="confirmRemove"
    />
    <MDBModal v-model="showDeletedModal" v-show="showDeletedModal" tabindex="-1" centered>
      <MDBCard class="border-0">
        <MDBCardBody class="p-4">
          <MDBCardTitle class="mb-3">{{ $t("shop.deletedProductsTitle") }}</MDBCardTitle>
          <MDBCardText class="mb-3">
            {{ $t("shop.deletedProductsMessage") }}
          </MDBCardText>
          <ul class="mb-4 ps-3">
            <li v-for="(name, index) in deletedItemNames" :key="`deleted_${index}`">{{ name }}</li>
          </ul>
          <div class="d-flex justify-content-end">
            <MDBBtn color="primary" @click="showDeletedModal = false">
              {{ $t("global.buttons.close") }}
            </MDBBtn>
          </div>
        </MDBCardBody>
      </MDBCard>
    </MDBModal>
  </MDBContainer>
</template>

<script setup>
import Loader from "@/components/General/Loader/index.vue";
import RemoveItemModal from "@/components/Cart/RemoveItemModal.vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import {
  MDBContainer,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBCardText,
  MDBBtn,
  MDBCardFooter,
  MDBTable,
  MDBModal,
} from "mdb-vue-ui-kit";

import NumberInput from "@/components/Shared/NumberInput.vue";
import { onMounted, computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { toast } from "vue3-toastify";

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const timeout = ref(null);
const loading = ref(true)
const pending = ref(false)
const showRemoveModal = ref(false);
const removeItemToken = ref(null);
const removeItemName = ref("");
const removeModalMessage = ref("");
const removeModalItemNames = ref([]);
const removeModalMode = ref("single");
const selectedItemTokens = ref([]);
const showDeletedModal = ref(false);

const cart = computed(() => store.getters.getCart);
const user = computed(() => store.getters.getMe);
const currentLocale = computed(() => store.getters.getCurrentLocale);
const isItemDeleted = (item) => {
  return Boolean(item?.product?.deleted_at || item?.product_variant?.deleted_at);
};

const deletedItems = computed(() =>
  (cart.value?.items || []).filter((item) => isItemDeleted(item))
);
const getProductName = (product) => {
  if (!product) return "";
  const localizedName = product?.name?.[currentLocale.value];
  if (localizedName) return localizedName;
  const fallbackName =
    product?.name && typeof product.name === "object"
      ? Object.values(product.name).find((value) => value)
      : product?.name;
  return fallbackName || product?.sku || product?.id || "";
};

const deletedItemNames = computed(() =>
  deletedItems.value.map((item) => getProductName(item?.product)).filter((name) => name)
);

const getPointsPerUnit = (item) => {
  return item.product_variant
    ? item.product_variant.points
    : item.product?.points;
};

const getMaxQuantityForItem = (item) => {
  const pointsPerUnit = getPointsPerUnit(item);
  if (!pointsPerUnit) {
    return 1;
  }

  const availPoints = user.value?.points?.avail_points ?? 0;
  const otherItemsTotal = (cart.value?.total ?? 0) - (item.points ?? 0);
  const maxAffordable = Math.floor((availPoints - otherItemsTotal) / pointsPerUnit);

  return Math.max(1, maxAffordable);
};

const showNotEnoughPointsToast = () => {
  toast(t("shop.notEnoughPoints"), {
    autoClose: 10000,
    type: "warning",
    theme: "colored",
    transition: toast.TRANSITIONS.SLIDE,
    position: toast.POSITION.BOTTOM_RIGHT,
  });
};

const overPoints = computed(() => {
  return user.value.points.avail_points >= cart.value.total;
});

const toOrderDelivery = () => {
  router.push({
    name: "orderDelivery",
  });
};

const handleContinue = () => {
  if (deletedItems.value.length > 0) {
    showDeletedModal.value = true;
    return;
  }
  toOrderDelivery();
};

const showPoints = (product) => {
  return product.product_variant
    ? product.product_variant.points
    : product.product.points;
};

const back = () => {
  router.go(-1);
};

const showAttributes = (product) => {
  if (product.product_variant) {
    if (product.product_variant.title) {
      return product.product_variant.title[currentLocale.value];
    }

    const attributeOption = product.product_variant.attribute_option;
    if (!attributeOption) {
      return "-";
    }

    try {
      const parsed =
        typeof attributeOption === "string"
          ? JSON.parse(attributeOption)
          : Array.isArray(attributeOption)
            ? attributeOption
            : [];

      if (!Array.isArray(parsed) || parsed.length === 0) {
        return "-";
      }

      return parsed
        .map((entry) => `${entry?.label ?? ""}: ${entry?.option ?? ""}`.trim())
        .filter((entry) => entry !== ":" && entry.length > 0)
        .join(", ");
    } catch {
      return "-";
    }
  }

  return "-";
};

const resetRemoveModalState = () => {
  removeItemToken.value = null;
  removeItemName.value = "";
  removeModalMessage.value = "";
  removeModalItemNames.value = [];
  removeModalMode.value = "single";
};

const openRemoveModal = (item) => {
  resetRemoveModalState();
  removeModalMode.value = "single";
  removeItemToken.value = item?.token ?? null;
  removeItemName.value = getProductName(item?.product);
  showRemoveModal.value = true;
};

const openRemoveSelectedModal = () => {
  if (selectedItemTokens.value.length === 0) {
    return;
  }

  resetRemoveModalState();
  removeModalMode.value = "selected";
  removeModalMessage.value = t("shop.removeSelectedConfirm");
  removeModalItemNames.value = (cart.value?.items || [])
    .filter((item) => selectedItemTokens.value.includes(item.token))
    .map((item) => getProductName(item?.product))
    .filter(Boolean);
  showRemoveModal.value = true;
};

const openClearCartModal = () => {
  resetRemoveModalState();
  removeModalMode.value = "all";
  removeModalMessage.value = t("shop.clearCartConfirm");
  removeModalItemNames.value = (cart.value?.items || [])
    .map((item) => getProductName(item?.product))
    .filter(Boolean);
  showRemoveModal.value = true;
};

const selectAllItems = () => {
  selectedItemTokens.value = (cart.value?.items || [])
    .map((item) => item.token)
    .filter(Boolean);
};

const deselectAllItems = () => {
  selectedItemTokens.value = [];
};

const confirmRemove = async () => {
  showRemoveModal.value = false;
  pending.value = true;

  try {
    if (removeModalMode.value === "single") {
      if (!removeItemToken.value) {
        return;
      }
      await store.dispatch("deleteItem", removeItemToken.value);
      selectedItemTokens.value = selectedItemTokens.value.filter(
        (token) => token !== removeItemToken.value,
      );
    } else if (removeModalMode.value === "selected") {
      const tokens = [...selectedItemTokens.value];
      if (tokens.length === 0) {
        return;
      }
      await store.dispatch("deleteItems", tokens);
      selectedItemTokens.value = [];
    } else if (removeModalMode.value === "all") {
      const tokens = (cart.value?.items || []).map((item) => item.token).filter(Boolean);
      if (tokens.length === 0) {
        return;
      }
      await store.dispatch("deleteItems", tokens);
      selectedItemTokens.value = [];
    }
  } finally {
    pending.value = false;
    resetRemoveModalState();
  }
};

watch(
  () => cart.value?.items,
  (items) => {
    const validTokens = new Set((items || []).map((item) => item.token).filter(Boolean));
    selectedItemTokens.value = selectedItemTokens.value.filter((token) =>
      validTokens.has(token),
    );
  },
);

onMounted(async () => {
  loading.value = true
  await store.dispatch("fetchUserShopCart");
  loading.value = false
});

const changeQuantity = async (quantity, item) => {
  const maxQuantity = getMaxQuantityForItem(item);

  if (quantity > maxQuantity) {
    showNotEnoughPointsToast();
    item.quantity = maxQuantity;
    return;
  }

  if (quantity < 1) {
    item.quantity = 1;
    return;
  }

  pending.value = true;
  clearTimeout(timeout.value);

  timeout.value = setTimeout(async () => {
    const data = {
      qty: quantity,
      id: item.id,
    };

    await store.dispatch("updateQuantity", data);
    await store.dispatch("fetchUserShopCart");
    pending.value = false;
  }, 1000);
};
</script>

<style scoped lang="scss">
.cart-select-col {
  width: 40px;
  vertical-align: middle;
}
</style>
